import { Bell } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useUnreadNotificationsCount } from '@/core/api/notifications';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
} from '@/shared/components/ui';
import type { RouteProp } from '@/shared/types/route-prop';

import { LanguageSwitcher } from './language-switcher';
import { Logo } from './logo';
import { NavUser } from './nav-user';
import { SidebarLink } from './sidebar-link';

interface SidebarProps {
  routes: RouteProp[];
}

export const Sidebar = ({ routes }: SidebarProps) => {
  const { t } = useIntl();
  const location = useLocation();
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const { user } = useAuth();

  const notificationsPath =
    user?.role === 'customer-admin' ? '/c/notifications' : '/gmn/notifications';
  const isNotificationsActive = location.pathname.startsWith(notificationsPath);

  return (
    <ShadcnSidebar variant="sidebar">
      <SidebarHeader>
        <Logo />
      </SidebarHeader>
      <SidebarContent className="flex flex-col">
        <SidebarGroup className="py-0">
          <SidebarMenuButton asChild data-active={isNotificationsActive}>
            <Link to={notificationsPath} className="flex justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <Bell className="size-4" />
                <span className="truncate">{t('common.notifications')}</span>
              </div>

              {unreadCount > 0 && (
                <span
                  className={`
                    flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none
                    text-primary-foreground
                  `}
                >
                  {unreadCount}
                </span>
              )}
            </Link>
          </SidebarMenuButton>
        </SidebarGroup>

        <SidebarGroup className="pt-0">
          <SidebarGroupLabel>{t('common.navigation')}</SidebarGroupLabel>
          <SidebarMenu>
            {routes.map((route) => (
              <SidebarLink
                key={route.to}
                route={route}
                currentPath={location.pathname}
                isActive={location.pathname.startsWith(route.to)}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>

        <div className="mt-auto">
          <SidebarGroup>
            <SidebarGroupContent>
              <LanguageSwitcher />
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </ShadcnSidebar>
  );
};
